import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import type {
  JsonIslandSpec,
  MarketplaceDefinitionConfig,
} from '../types/marketplaceDefinition';

type JsonIslandsPageType = 'search_results' | 'product_listing';

interface JsonIslandsEditorProps {
  pageType: JsonIslandsPageType;
  config: MarketplaceDefinitionConfig;
  setConfig: Dispatch<SetStateAction<MarketplaceDefinitionConfig>>;
}

const PAGE_LABELS: Record<JsonIslandsPageType, string> = {
  search_results: 'search results',
  product_listing: 'product listing',
};

const ISLAND_KINDS: JsonIslandSpec['kind'][] = [
  'ld_json',
  'window_var',
  'script_id',
  'inline_regex',
  'inline_regex_all',
];

const LD_JSON_PRODUCT_PRESET: JsonIslandSpec[] = [
  { name: 'product_ld', kind: 'ld_json', target: 'Product' },
];

const LD_JSON_ITEMLIST_PRESET: JsonIslandSpec[] = [
  { name: 'ld_json', kind: 'ld_json', target: 'ItemList' },
];

export default function JsonIslandsEditor({
  pageType,
  config,
  setConfig,
}: JsonIslandsEditorProps) {
  const page = config.pages?.[pageType];
  const islands: JsonIslandSpec[] = page?.json_islands || [];

  const updatePage = (patch: Record<string, unknown>) => {
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageType]: {
          ...prev.pages?.[pageType],
          page_type: pageType,
          ...patch,
        },
      },
    }));
  };

  const updateIslands = (next: JsonIslandSpec[]) => {
    updatePage({ json_islands: next.length ? next : undefined });
  };

  const updateIsland = (idx: number, patch: Partial<JsonIslandSpec>) => {
    const next = [...islands];
    next[idx] = { ...next[idx], ...patch };
    updateIslands(next);
  };

  const removeIsland = (idx: number) => {
    const next = [...islands];
    next.splice(idx, 1);
    updateIslands(next);
  };

  const applyProductLdPreset = () => {
    updatePage({
      json_islands: LD_JSON_PRODUCT_PRESET,
    });
  };

  const applyItemListLdPreset = () => {
    updatePage({
      json_islands: LD_JSON_ITEMLIST_PRESET,
      json_items_path: 'ld_json:$.itemListElement[*].item',
      json_total_results_path: 'ld_json:$.numberOfItems',
    });
  };

  const pageLabel = PAGE_LABELS[pageType];

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="subtitle1">JSON Islands</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {pageType === 'product_listing' && (
            <Button size="small" variant="outlined" onClick={applyProductLdPreset}>
              Preset: JSON-LD Product
            </Button>
          )}
          {pageType === 'search_results' && (
            <Button size="small" variant="outlined" onClick={applyItemListLdPreset}>
              Preset: JSON-LD ItemList
            </Button>
          )}
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() =>
              updateIslands([
                ...islands,
                { name: 'ld_json', kind: 'ld_json', target: 'Product' },
              ])
            }
          >
            Add Island
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Embedded JSON blobs in the page HTML. Use <strong>ld_json</strong> for schema.org{' '}
        <code>&lt;script type=&quot;application/ld+json&quot;&gt;</code> blocks (Product,
        ItemList, etc.). Reference islands in field selectors as{' '}
        <code>island_name:$.json.path</code> with selector type JSONPath.
      </Typography>

      {islands.map((island, idx) => (
        <Accordion key={idx} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {island.name || `island_${idx + 1}`}: {island.kind || 'ld_json'} →{' '}
              {island.target || '(no target)'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name"
                  value={island.name || ''}
                  onChange={e => updateIsland(idx, { name: e.target.value })}
                  placeholder="product_ld"
                  helperText="Used in qualified paths (name:$.path)"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Kind</InputLabel>
                  <Select
                    label="Kind"
                    value={island.kind || 'ld_json'}
                    onChange={e =>
                      updateIsland(idx, { kind: e.target.value as JsonIslandSpec['kind'] })
                    }
                  >
                    {ISLAND_KINDS.map(kind => (
                      <MenuItem key={kind} value={kind}>
                        {kind}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Target"
                  value={island.target || ''}
                  onChange={e => updateIsland(idx, { target: e.target.value })}
                  placeholder={
                    island.kind === 'ld_json'
                      ? 'Product or ItemList'
                      : island.kind === 'window_var'
                        ? 'g_page_config'
                        : island.kind === 'script_id'
                          ? '__NEXT_DATA__'
                          : 'regex with capturing group'
                  }
                  helperText={
                    island.kind === 'ld_json'
                      ? 'schema.org @type to match'
                      : island.kind === 'inline_regex'
                        ? 'Regex with one capturing group for JSON'
                        : island.kind === 'inline_regex_all'
                          ? 'Regex with one capturing group; collects ALL matches into a list'
                          : 'Variable name or script element id'
                  }
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <IconButton color="error" onClick={() => removeIsland(idx)} aria-label="Remove island">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {pageType === 'search_results' && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Search JSON paths
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              When <strong>json_items_path</strong> is set, listings are extracted from JSON instead
              of CSS item_selector. Stage 2 product scraping still runs when enabled.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="json_items_path"
              value={page?.json_items_path || ''}
              onChange={e =>
                updatePage({
                  json_items_path: e.target.value || undefined,
                })
              }
              placeholder="ld_json:$.itemListElement[*].item"
              helperText="Array of listing objects"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="json_next_page_path"
              value={page?.json_next_page_path || ''}
              onChange={e =>
                updatePage({
                  json_next_page_path: e.target.value || undefined,
                })
              }
              placeholder="page_config:$.mods.pager.data.nextUrl"
              helperText="Next page URL"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="json_total_results_path"
              value={page?.json_total_results_path || ''}
              onChange={e =>
                updatePage({
                  json_total_results_path: e.target.value || undefined,
                })
              }
              placeholder="ld_json:$.numberOfItems"
              helperText="Total result count"
            />
          </Grid>
        </Grid>
      )}

      {pageType === 'product_listing' && islands.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Example product fields: price → <code>product_ld:$.offers.price</code>, itemNumber →{' '}
          <code>product_ld:$.sku</code> (selector type JSONPath).
        </Typography>
      )}

      {islands.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No JSON islands configured for {pageLabel}. Add one or use a preset for schema.org
          JSON-LD.
        </Typography>
      )}
    </Box>
  );
}
